import { currentUserSelector } from "Auth/state";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Edit } from "shared/assets/icons";

import { FormBuilder, Modal } from "shared/components";
import { setMessageModal } from "shared/components/MessageModal";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { buildUrl } from "shared/utils";
import { useVenuePageContext } from "Venues/contexts";
import { VENUE_PATHS } from "Venues/router";
import { dataMetaSelector, stageSelectorCreator, suggestEdit } from "Venues/state";
import {
  IEquipmentCategory,
  ISuggestEdit,
  ISuggestEditBreadcrumbItem,
  VENUE_ACTIONS,
} from "Venues/types";
import {
  getEquipmentMainCategory,
  getInfoBlockItem,
  useClearActionMetaOnUnMount,
} from "Venues/utils";
import { fields, getDefaultValues } from "./formData";

export type SuggestEditFormType =
  | "Venue"
  | "Stage"
  | "StageInfoBlock"
  | "VenueInfoBlock"
  | "StageEquipmentCategory";

interface SuggestEditFormProps {
  type: SuggestEditFormType;
  handleClose: () => void;
  stageId?: string;
  infoBlockSlug?: string;
  categorySlug?: string;
}

export const SuggestEditForm: React.FC<SuggestEditFormProps> = ({
  type,
  infoBlockSlug,
  categorySlug,
  stageId,
  handleClose,
}) => {
  const { venue, venueId } = useVenuePageContext();
  const stageIdSelector = useMemo(() => stageSelectorCreator(stageId || ""), [stageId]);
  const stage = useSelector(stageIdSelector);
  const currentUser = useSelector(currentUserSelector);

  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.suggestEdit));
  const dispatch = useDispatch();

  useClearActionMetaOnUnMount(VENUE_ACTIONS.suggestEdit);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: { children: copy.suggestEdit.cta },
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  const defaultValues = useMemo(() => getDefaultValues(currentUser), [currentUser]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        setMessageModal({
          message: {
            status: IStatus.success,
            body: copy.suggestEdit.success,
          },
        })
      );
      handleClose();
    }
  }, [dispatch, apiMeta, handleClose]);

  const getEditLink = (): string => {
    const editLink = [window.location.origin];
    switch (type) {
      case "Stage":
        editLink.push(
          buildUrl(VENUE_PATHS.EDIT_STAGE, {
            venueId,
            stageId: stageId as string,
          })
        );
        break;
      case "Venue":
        editLink.push(buildUrl(VENUE_PATHS.EDIT_VENUE, { venueId }));
        break;
      case "VenueInfoBlock":
        editLink.push(
          buildUrl(VENUE_PATHS.EDIT_VENUE_INFO_BLOCK, {
            venueId,
            infoBlockSlug: infoBlockSlug || "",
          })
        );
        break;
      case "StageInfoBlock":
        editLink.push(
          buildUrl(VENUE_PATHS.EDIT_STAGE_INFO_BLOCK, {
            venueId,
            stageId: stageId as string,
            infoBlockSlug: infoBlockSlug || "",
          })
        );
        break;
      case "StageEquipmentCategory":
        editLink.push(
          buildUrl(VENUE_PATHS.EDIT_STAGE_EQUIPMENT, {
            venueId,
            stageId: stageId as string,
            categorySlug: categorySlug || "",
          })
        );
        break;
    }
    return editLink.join("");
  };

  const breadcrumb: ISuggestEditBreadcrumbItem[] = useMemo(() => {
    const breadCrumb: ISuggestEditBreadcrumbItem[] = [{ type: "Venue", title: venue?.title || "" }];

    if (["Stage", "StageInfoBlock", "StageEquipmentCategory"].includes(type)) {
      breadCrumb.push({
        type: "Stage",
        title: stage?.title || "",
      });
    }

    if (["StageInfoBlock", "VenueInfoBlock"].includes(type)) {
      const infoBlock = getInfoBlockItem(infoBlockSlug || "");
      breadCrumb.push({
        type: null,
        title: `${infoBlock?.parentLabel}: ${infoBlock?.label}` || "",
      });
    }

    if (["StageEquipmentCategory"].includes(type)) {
      breadCrumb.push({
        type: null,
        title: getEquipmentMainCategory(categorySlug as IEquipmentCategory)?.label || "",
      });
    }

    return breadCrumb;
  }, [stage, type, venue, categorySlug, infoBlockSlug]);

  const title = useMemo(() => {
    return breadcrumb.map(({ title }, index) => {
      if (index === 0) {
        return `${copy.suggestEdit.title} ${title}`;
      }
      return title;
    });
  }, [breadcrumb]);

  const onSubmit = (data: ISuggestEdit) => {
    const venue = API_ENDPOINTS.VENUE(venueId);
    const editLink = getEditLink();

    dispatch(suggestEdit({ data: { ...data, venue, editLink, breadcrumb }, venueId }));
  };

  return (
    <Modal title={title} icon={Edit} handleClose={handleClose}>
      <FormBuilder
        fields={fields}
        defaultValues={defaultValues}
        buttons={buttons}
        onSubmit={onSubmit}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiErrorMessage={!!apiMeta?.error}
        withCaptcha
      />
    </Modal>
  );
};
